import { createStore } from 'zustand/vanilla'
import { readJson, writeJson } from '../services/storage'

// @capacitor/preferences is NOT a dependency, so this uses localStorage — which in a
// Capacitor WebView is the app-private sandbox, each app having its own origin. Swapping to
// Preferences later means changing only the load/persist helpers.

const STOP_KEY      = 'transit_favorites'
const ROUTE_KEY     = 'transit_favorite_routes'
const BUS_STOP_KEY  = 'transit_favorite_bus_stops'
const BUS_ROUTE_KEY = 'transit_favorite_bus_routes'

/** A favorited bus stop. Unlike subway stations, bus stops are in no always-loaded metadata
 *  map (the ~15k list is lazy), so the display name is persisted alongside the id. */
export interface BusStopFavorite {
  id: string
  name: string
}

function loadIds(key: string): string[] {
  return readJson(key, [], decodeIds)
}

function persistIds(key: string, ids: Set<string>): void {
  writeJson(key, [...ids])
}

function decodeIds(value: unknown): string[] | undefined {
  return Array.isArray(value)
    ? value.filter((x): x is string => typeof x === 'string')
    : undefined
}

function loadBusStops(key: string): BusStopFavorite[] {
  return readJson(key, [], decodeBusStops)
}

function persistBusStops(key: string, stops: BusStopFavorite[]): void {
  writeJson(key, stops)
}

function decodeBusStops(value: unknown): BusStopFavorite[] | undefined {
  if (!Array.isArray(value)) return undefined
  return value.filter(
    (x): x is BusStopFavorite =>
      !!x && typeof x === 'object' &&
      typeof (x as BusStopFavorite).id === 'string' &&
      typeof (x as BusStopFavorite).name === 'string',
  )
}

interface FavoritesState {
  /** Favorited subway stop/station IDs. */
  favorites: Set<string>
  toggleFavorite: (id: string) => void
  isFavorite: (id: string) => boolean

  /** Favorited subway route IDs (e.g. '7', 'A', 'SI'). */
  favoriteRoutes: Set<string>
  toggleFavoriteRoute: (routeId: string) => void
  isFavoriteRoute: (routeId: string) => boolean

  /** Favorited bus route IDs. Kept apart from the subway favoriteRoutes so the Favorites
   *  panel can group by borough and the two id spaces ('B' vs 'B41') never collide. */
  favoriteBusRoutes: Set<string>
  toggleFavoriteBusRoute: (routeId: string) => void
  isFavoriteBusRoute: (routeId: string) => boolean

  /** Favorited bus stops (id + display name). */
  favoriteBusStops: BusStopFavorite[]
  toggleFavoriteBusStop: (stop: BusStopFavorite) => void
  isFavoriteBusStop: (id: string) => boolean
}

export const useFavoritesStore = createStore<FavoritesState>((set, get) => ({
  favorites: new Set<string>(loadIds(STOP_KEY)),

  toggleFavorite: (id) =>
    set((state) => {
      if (!id) return state
      const next = new Set(state.favorites)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return { favorites: next }
    }),

  isFavorite: (id) => get().favorites.has(id),

  favoriteRoutes: new Set<string>(loadIds(ROUTE_KEY)),

  toggleFavoriteRoute: (routeId) =>
    set((state) => {
      if (!routeId) return state
      const next = new Set(state.favoriteRoutes)
      if (next.has(routeId)) next.delete(routeId)
      else next.add(routeId)
      return { favoriteRoutes: next }
    }),

  isFavoriteRoute: (routeId) => get().favoriteRoutes.has(routeId),

  favoriteBusRoutes: new Set<string>(loadIds(BUS_ROUTE_KEY)),

  toggleFavoriteBusRoute: (routeId) =>
    set((state) => {
      if (!routeId) return state
      const next = new Set(state.favoriteBusRoutes)
      if (next.has(routeId)) next.delete(routeId)
      else next.add(routeId)
      return { favoriteBusRoutes: next }
    }),

  isFavoriteBusRoute: (routeId) => get().favoriteBusRoutes.has(routeId),

  favoriteBusStops: loadBusStops(BUS_STOP_KEY),

  toggleFavoriteBusStop: (stop) =>
    set((state) => {
      if (!stop?.id) return state
      const exists = state.favoriteBusStops.some((s) => s.id === stop.id)
      const next = exists
        ? state.favoriteBusStops.filter((s) => s.id !== stop.id)
        : [...state.favoriteBusStops, { id: stop.id, name: stop.name }]
      return { favoriteBusStops: next }
    }),

  isFavoriteBusStop: (id) => get().favoriteBusStops.some((s) => s.id === id),
}))

useFavoritesStore.subscribe((state, prev) => {
  if (state.favorites !== prev.favorites)
    persistIds(STOP_KEY, state.favorites)
  if (state.favoriteRoutes !== prev.favoriteRoutes)
    persistIds(ROUTE_KEY, state.favoriteRoutes)
  if (state.favoriteBusRoutes !== prev.favoriteBusRoutes)
    persistIds(BUS_ROUTE_KEY, state.favoriteBusRoutes)
  if (state.favoriteBusStops !== prev.favoriteBusStops)
    persistBusStops(BUS_STOP_KEY, state.favoriteBusStops)
})
